import { logger } from "@/utils/logger";

/**
 * Shift Digital tracking utility
 * Fires sd() events for dealers with "shift" in their OEM name
 */

declare global {
  interface Window {
    sd?: (...args: any[]) => void;
    __sdOriginal?: (...args: any[]) => void;
    __sdProxy?: (...args: any[]) => void;
    __shiftPageviewFired?: boolean;
    __shiftPageviewCallbacks?: (() => void)[];
  }
}

/**
 * Track if pageview has been detected and manage callbacks
 */
let pageviewDetected = false;
const pageviewCallbacks: (() => void)[] = [];

/**
 * Install a proxy on window.sd to detect when PAGEVIEW fires
 * This allows us to fire chat events AFTER the pageview
 */
export const installShiftDigitalProxy = (): void => {
  if (typeof window === 'undefined') return;

  // If sd is already our proxy, nothing to do
  if (window.sd && typeof window.sd === 'function' && (window.sd as any).__isShiftProxy) {
    return;
  }

  // Try wrapping immediately if available
  if (window.sd && typeof window.sd === 'function') {
    wrapSdFunction();
  }

  // Watch for Shift script loading / overwriting sd for up to 60 seconds
  const startedAt = Date.now();
  const checkInterval = setInterval(() => {
    const sd = window.sd;
    const isProxy = !!sd && typeof sd === 'function' && (sd as any).__isShiftProxy === true;

    if (sd && typeof sd === 'function' && !isProxy) {
      wrapSdFunction();
    }

    if (Date.now() - startedAt > 60000) {
      clearInterval(checkInterval);
    }
  }, 250);
};

/**
 * Wrap the sd() function to intercept calls
 */
const wrapSdFunction = (): void => {
  if (typeof window === 'undefined') return;

  const currentSd = window.sd;
  if (!currentSd || typeof currentSd !== 'function') return;

  // If already proxied, do nothing
  if ((currentSd as any).__isShiftProxy === true) return;

  // Capture the current sd() as the original implementation
  const originalSd = currentSd;
  window.__sdOriginal = originalSd;

  // Use a real function (not an arrow) to preserve dynamic `this` binding.
  const proxiedSd = function (this: any, ...args: any[]) {
    // Detect pageview events
    try {
      if (args[0] === 'dataLayer' && args[1]) {
        const eventData = args[1];
        if (
          eventData.events === 'PAGEVIEW' ||
          eventData.event === 'PAGEVIEW' ||
          eventData.events?.toLowerCase() === 'pageview' ||
          eventData.event?.toLowerCase() === 'pageview'
        ) {
          pageviewDetected = true;
          window.__shiftPageviewFired = true;

          // Execute all waiting callbacks after a tiny buffer to ensure pageview completes
          setTimeout(() => {
            pageviewCallbacks.forEach(cb => cb());
            pageviewCallbacks.length = 0;
          }, 50);
        }
      }
    } catch {
      // Ignore detection errors; never break host tracking
    }

    return (originalSd as any).apply(this ?? window, args);
  };

  // IMPORTANT: Preserve any properties the host page attaches to sd() (queues, timestamps, etc.)
  // Replacing sd() without copying these can break Shift's bootstrapping.
  try {
    const descriptors = Object.getOwnPropertyDescriptors(originalSd);
    for (const [key, desc] of Object.entries(descriptors)) {
      // Skip non-configurable built-ins (and our marker)
      if (
        key === 'length' ||
        key === 'name' ||
        key === 'arguments' ||
        key === 'caller' ||
        key === 'prototype' ||
        key === '__isShiftProxy'
      ) {
        continue;
      }
      try {
        Object.defineProperty(proxiedSd, key, desc);
      } catch {
        // Best-effort; never fail wrapping due to a property edge-case
      }
    }

    for (const sym of Object.getOwnPropertySymbols(originalSd)) {
      const desc = Object.getOwnPropertyDescriptor(originalSd, sym);
      if (!desc) continue;
      try {
        Object.defineProperty(proxiedSd, sym, desc);
      } catch {
        // Best-effort
      }
    }
  } catch {
    // Best-effort
  }

  (proxiedSd as any).__isShiftProxy = true;
  window.__sdProxy = proxiedSd;
  window.sd = proxiedSd as any;
};

/**
 * Wait for pageview to fire before executing callback.
 *
 * Why this exists:
 * - Shift Digital requires PAGEVIEW to happen before chat events (chatImpression, etc.)
 * - A fixed delay is unreliable because the Shift script may load late (events would fail)
 * - We wrap sd() (safely) to detect PAGEVIEW and release queued callbacks
 */
export const waitForShiftPageview = (callback: () => void): void => {
  if (typeof window === 'undefined') return;

  // Ensure we can detect PAGEVIEW even if Shift loads after the widget.
  installShiftDigitalProxy();

  if (window.__shiftPageviewFired || pageviewDetected) {
    setTimeout(callback, 0);
    return;
  }

  pageviewCallbacks.push(callback);
};

// NOTE: Do NOT auto-install the sd() proxy on module import.
// This script is loaded on many pages (even where the chat UI doesn't mount), and
// proxying window.sd can interfere with the host site's Shift Digital tracking.

export interface ShiftDigitalEventData {
  chatProvider?: string;
  chatProfitCenter?: string;
  chatType?: string;
  chatMessageType?: string;
  chatSessionId?: string;
  chatMessageContent?: string;
  chatLeadId?: string;
  events?: string;
}

/**
 * Check if sd() function is available
 */
export const isShiftDigitalAvailable = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  // Check both original and proxied
  const sdExists = window.sd !== undefined || window.__sdOriginal !== undefined;
  const sdIsFunction = typeof window.sd === 'function' || typeof window.__sdOriginal === 'function';

  return sdExists && sdIsFunction;
};

/**
 * Check if dealer should use Shift Digital tracking (OEM name contains "shift")
 */
export const shouldUseShiftDigitalTracking = (certificationName?: string): boolean => {
  if (!certificationName) {
    return false;
  }
  const shouldTrack = certificationName.toLowerCase().includes('shift');
  return shouldTrack;
};

/**
 * Fire a Shift Digital event
 * Uses the original sd function if we've proxied it, otherwise uses window.sd
 */
export const fireShiftDigitalEvent = (eventData: ShiftDigitalEventData): boolean => {
  try {
    if (typeof window === 'undefined') {
      return false;
    }

    // Prefer the underlying Shift implementation if we captured it
    const sdFn = window.__sdOriginal || window.sd;

    if (!sdFn || typeof sdFn !== 'function') {
      logger.log('🔵 Shift Digital: sd() function not available');
      return false;
    }

    logger.log('🔵 Shift Digital: Firing event', eventData.events);

    // IMPORTANT: preserve `this` binding in case sd() depends on window context
    (sdFn as any).call(window, 'dataLayer', eventData);
    (sdFn as any).call(window, 'send');

    return true;
  } catch (error) {
    console.error('🔵 Shift Digital: Error firing event:', error);
    return false;
  }
};

/**
 * Determine profit center based on page context or form type
 */
export const determineProfitCenter = (formType?: string): string => {
  if (!formType) {
    // Try to determine from page URL or context
    if (typeof window !== 'undefined') {
      const url = window.location.href.toLowerCase();
      if (url.includes('/service')) return 'service';
      if (url.includes('/parts')) return 'parts';
    }
    return 'sales'; // Default
  }

  // Map form types to profit centers
  const formTypeMap: Record<string, string> = {
    'service': 'service',
    'appointment': 'sales',
    'finance': 'sales',
    'trade': 'sales',
    'contact': 'sales',
    'carfax': 'sales',
    'offer': 'sales'
  };

  return formTypeMap[formType.toLowerCase()] || 'sales';
};

/**
 * Base event data that's common to all events
 */
const getBaseEventData = (sessionId: string, profitCenter: string = 'sales'): ShiftDigitalEventData => ({
  chatProvider: 'D2C Media',
  chatProfitCenter: profitCenter,
  chatType: 'chat',
  chatSessionId: sessionId
});

/**
 * Track chat impression (when chat loads)
 */
export const trackShiftChatImpression = (sessionId: string, profitCenter?: string): boolean => {
  const eventData: ShiftDigitalEventData = {
    ...getBaseEventData(sessionId, profitCenter),
    events: 'chatImpression'
  };
  
  return fireShiftDigitalEvent(eventData);
};

/**
 * Track chat click (when chat is expanded)
 */
export const trackShiftChatClick = (sessionId: string, profitCenter?: string): boolean => {
  const eventData: ShiftDigitalEventData = {
    ...getBaseEventData(sessionId, profitCenter),
    events: 'chatClick'
  };
  
  return fireShiftDigitalEvent(eventData);
};

/**
 * Track chat message (user or agent)
 */
export const trackShiftChatMessage = (
  sessionId: string, 
  messageContent: string,
  messageType: 'user' | 'agent',
  profitCenter?: string
): void => {
  const eventData: ShiftDigitalEventData = {
    ...getBaseEventData(sessionId, profitCenter),
    chatMessageType: messageType,
    chatMessageContent: messageContent,
    events: 'chatMessage'
  };
  
  fireShiftDigitalEvent(eventData);
};

/**
 * Track lead submission
 */
export const trackShiftChatLead = (
  sessionId: string, 
  leadId: string, 
  profitCenter?: string
): void => {
  const eventData: ShiftDigitalEventData = {
    ...getBaseEventData(sessionId, profitCenter),
    chatLeadId: leadId,
    events: 'chatLead'
  };
  
  fireShiftDigitalEvent(eventData);
};

/**
 * Track chat closed
 */
export const trackShiftChatClosed = (sessionId: string, profitCenter?: string): void => {
  const eventData: ShiftDigitalEventData = {
    ...getBaseEventData(sessionId, profitCenter),
    events: 'chatClosed'
  };
  
  fireShiftDigitalEvent(eventData);
};

/**
 * DEBUG: Force fire an event to test sd() is working
 * Call from console: window.testShiftDigital()
 */
export const debugForceFireShiftEvent = (): void => {
  logger.log('🔵 [ShiftDigital] Testing event fire...');
  logger.log('🔵 [ShiftDigital] window.sd:', typeof window.sd);
  logger.log('🔵 [ShiftDigital] window.__sdOriginal:', typeof window.__sdOriginal);
  
  const testEventData: ShiftDigitalEventData = {
    chatProvider: 'D2C Media',
    chatProfitCenter: 'sales',
    chatType: 'chat',
    chatSessionId: 'TEST-' + Date.now(),
    events: 'chatImpression'
  };
  
  const result = fireShiftDigitalEvent(testEventData);
  logger.log('🔵 [ShiftDigital] Event fired:', result);
};

/**
 * DEBUG: Get current Shift Digital status
 */
export const debugShiftStatus = (): void => {
  logger.log('🔵 [ShiftDigital] Status:');
  logger.log('  - window.sd exists:', typeof window.sd === 'function');
  logger.log('  - window.__sdOriginal exists:', typeof window.__sdOriginal === 'function');
  logger.log('  - sd is proxy:', !!(window.sd as any)?.__isShiftProxy);
  logger.log('  - pageview fired:', window.__shiftPageviewFired || false);
  logger.log('  - isShiftDigitalAvailable():', isShiftDigitalAvailable());
};

// Expose to window for testing - use a function that re-exposes on each call
// to handle timing issues with widget loading
const exposeDebugFunctions = () => {
  if (typeof window !== 'undefined') {
    (window as any).testShiftDigital = debugForceFireShiftEvent;
    (window as any).shiftStatus = debugShiftStatus;
  }
};

// Expose immediately
exposeDebugFunctions();

// Also re-expose after a delay to handle widget loading
if (typeof window !== 'undefined') {
  setTimeout(exposeDebugFunctions, 1000);
  setTimeout(exposeDebugFunctions, 3000);
}
